import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Lock, Eye, EyeOff, ShieldCheck, Stethoscope, MapPin, Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEOHead from '../components/SEOHead';
import { toast } from 'sonner';
import { SearchableCombobox } from '../components/SearchableCombobox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DENTAL_SPECIALTY_TERMS = [
  'dent',
  'odont',
  'bucomaxilo',
  'endodont',
  'periodont',
  'ortodont',
  'implantodont',
  'protese dentaria',
  'odontopedi',
  'estomatologia',
  'radiologia odont',
  'cirurgiao dentista',
];

const normalizeSearchText = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const getSpecialtyText = (specialty) => normalizeSearchText([
  specialty?.name,
  specialty?.slug,
  ...(Array.isArray(specialty?.tags) ? specialty.tags : []),
].filter(Boolean).join(' '));

const isDentalSpecialty = (specialty) => {
  const text = getSpecialtyText(specialty);
  return DENTAL_SPECIALTY_TERMS.some((term) => text.includes(term));
};

const getSpecialtiesForProviderType = (professionalType, specialties) => {
  if (['dentista', 'clinica_odontologica'].includes(professionalType)) {
    return specialties.filter(isDentalSpecialty);
  }
  return specialties.filter((specialty) => !isDentalSpecialty(specialty));
};

const PROVIDER_TYPE_OPTIONS = [
  { value: 'medico', label: 'Médico', registrationType: 'crm', registrationLabel: 'CRM' },
  { value: 'dentista', label: 'Dentista', registrationType: 'cro', registrationLabel: 'CRO' },
  { value: 'clinica_medica', label: 'Clínica médica', registrationType: 'none', registrationLabel: 'CRM do responsável técnico' },
  { value: 'clinica_odontologica', label: 'Clínica odontológica', registrationType: 'none', registrationLabel: 'CRO do responsável técnico' },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { registerProvider } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [specialtyOpen, setSpecialtyOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);

  // Two paths: 'new' = preencher formulario do zero, 'validate' = redireciona /claim
  const [activeTab, setActiveTab] = useState('validate');

  // Provider form
  const [providerForm, setProviderForm] = useState({
    legal_name: '',
    trade_name: '',
    professional_type: 'medico',
    professional_registration_type: 'crm',
    professional_registration_number: '',
    professional_registration_uf: '',
    cnpj: '',
    technical_responsible_name: '',
    technical_responsible_registration_type: 'none',
    technical_responsible_registration_number: '',
    technical_responsible_registration_uf: '',
    address: '',
    neighborhood: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    password: '',
    responsible_name: '',
    description: '',
    specialty: '',
    specialty_id: '',
    crm: '',
  });

  useEffect(() => {
    let alive = true;
    const fetchFilters = async () => {
      setLoadingFilters(true);
      try {
        const [specialtiesRes, statesRes] = await Promise.all([
          axios.get(`${API_URL}/api/public/specialties`),
          axios.get(`${API_URL}/api/public/states`),
        ]);
        if (!alive) return;
        setSpecialties((specialtiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
        setStates((statesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
      } catch {
        if (alive) toast.error('Não foi possível carregar cidades e especialidades.');
      } finally {
        if (alive) setLoadingFilters(false);
      }
    };
    fetchFilters();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchCities = async () => {
      if (!providerForm.state) {
        setCities([]);
        return;
      }
      setLoadingCities(true);
      try {
        const res = await axios.get(`${API_URL}/api/public/cities?state=${providerForm.state}`);
        if (!alive) return;
        setCities((res.data || []).sort((a, b) => (a.city || '').localeCompare(b.city || '', 'pt-BR')));
      } catch {
        if (alive) toast.error('Não foi possível carregar as cidades deste estado.');
      } finally {
        if (alive) setLoadingCities(false);
      }
    };
    fetchCities();
    return () => { alive = false; };
  }, [providerForm.state]);

  const filteredSpecialties = React.useMemo(
    () => getSpecialtiesForProviderType(providerForm.professional_type, specialties),
    [providerForm.professional_type, specialties],
  );

  useEffect(() => {
    if (!selectedSpecialty) return;
    const isStillAllowed = filteredSpecialties.some((specialty) => specialty.id === selectedSpecialty.id);
    if (!isStillAllowed) {
      setSelectedSpecialty(null);
      setProviderForm((current) => ({ ...current, specialty: '', specialty_id: '' }));
    }
  }, [filteredSpecialties, selectedSpecialty]);

  const handleProviderSubmit = async (e) => {
    e.preventDefault();
    const typeConfig = PROVIDER_TYPE_OPTIONS.find((item) => item.value === providerForm.professional_type) || PROVIDER_TYPE_OPTIONS[0];
    const isIndividualProvider = ['medico', 'dentista'].includes(providerForm.professional_type);
    const isClinicProvider = ['clinica_medica', 'clinica_odontologica'].includes(providerForm.professional_type);
    const registrationNumber = providerForm.professional_registration_number.trim();
    const registrationUf = providerForm.professional_registration_uf || providerForm.state;

    if (!providerForm.trade_name.trim() || !providerForm.state || !providerForm.city || !providerForm.zip_code.trim() || !providerForm.phone.trim() || !providerForm.email.trim() || !providerForm.specialty.trim() || !providerForm.password.trim()) {
      toast.error('Preencha todos os campos para continuar.');
      return;
    }
    if (isIndividualProvider && (!registrationNumber || !registrationUf)) {
      toast.error(`Informe o ${typeConfig.registrationLabel} e a UF do registro.`);
      return;
    }
    if (isClinicProvider && !providerForm.cnpj.trim()) {
      toast.error('Informe o CNPJ da clínica para continuar.');
      return;
    }
    setLoading(true);

    try {
      const cleanName = providerForm.trade_name.trim();
      const cleanSpecialty = providerForm.specialty.trim();
      await registerProvider({
        ...providerForm,
        professional_registration_type: typeConfig.registrationType,
        professional_registration_number: registrationNumber,
        professional_registration_uf: registrationUf,
        crm: registrationNumber,
        cnpj: providerForm.cnpj.trim(),
        legal_name: cleanName,
        trade_name: cleanName,
        responsible_name: cleanName,
        address: providerForm.address || 'Não informado',
        neighborhood: providerForm.neighborhood || 'Não informado',
        description: cleanSpecialty ? `Especialidade: ${cleanSpecialty}` : '',
        specialty_id: providerForm.specialty_id,
        specialty_name: cleanSpecialty,
      });
      toast.success('Cadastro profissional criado com sucesso!');
      navigate('/prestador');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar profissional');
    } finally {
      setLoading(false);
    }
  };

  const updateProviderType = (professionalType) => {
    const next = PROVIDER_TYPE_OPTIONS.find((item) => item.value === professionalType) || PROVIDER_TYPE_OPTIONS[0];
    setSelectedSpecialty(null);
    setSpecialtyOpen(false);
    setProviderForm((current) => ({
      ...current,
      professional_type: next.value,
      professional_registration_type: next.registrationType,
      professional_registration_number: '',
      professional_registration_uf: current.state || '',
      technical_responsible_registration_type: next.value === 'clinica_odontologica' ? 'cro' : (next.value === 'clinica_medica' ? 'crm' : 'none'),
      technical_responsible_registration_number: '',
      technical_responsible_registration_uf: current.state || '',
      specialty: '',
      specialty_id: '',
    }));
  };

  const formatCNPJ = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatCEP = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1');
  };

  const lookupCep = async (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 8) return;

    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data?.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      setProviderForm((current) => {
        const nextState = data.uf || current.state;
        return {
          ...current,
          state: nextState,
          city: data.localidade || current.city,
          neighborhood: data.bairro || current.neighborhood,
          address: data.logradouro || current.address,
          professional_registration_uf: nextState || current.professional_registration_uf,
          technical_responsible_registration_uf: nextState || current.technical_responsible_registration_uf,
        };
      });
    } catch {
      toast.error('Falha ao buscar CEP. Preencha estado e cidade manualmente.');
    }
  };

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-white flex items-center justify-center p-4" data-testid="register-page">
      <SEOHead
        title="Cadastrar ou validar perfil | BuscaDoutor"
        description="Fluxo de cadastro ou validação de perfil no BuscaDoutor."
        canonicalUrl="/cadastro"
        robots="noindex, nofollow"
      />
      <div className="w-full max-w-lg">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <Logo size="lg" />
        </Link>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-8">
            {/* Validar Cadastro - redireciona para /claim */}
            {activeTab === 'validate' && (
              <div className="space-y-5 py-2" data-testid="tab-validate-content">
                <div className="rounded-2xl bg-emerald-50/60 border border-emerald-200/70 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-base mb-1.5" style={{fontFamily:'Outfit,sans-serif'}}>
                        {'Seu Perfil já consta no BuscaDoutor?'}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {'Vamos confirmar sua identidade pelo WhatsApp e liberar o acesso para você gerenciar o perfil. Leva 2 minutos.'}
                      </p>
                    </div>
                  </div>
                </div>

                <ul className="space-y-2.5 text-sm text-slate-600 pl-1">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">1.</span>
                    Encontre seu cadastro pelo nome ou telefone
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">2.</span>
                    {'Receba um código de 6 dígitos no WhatsApp'}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">3.</span>
                    Defina seu email e senha para acessar
                  </li>
                </ul>

                <Link to="/claim" data-testid="goto-claim-link">
                  <Button
                    type="button"
                    className="w-full h-12 bg-primary hover:bg-[#2D6D5D] text-white rounded-full"
                    data-testid="goto-claim-btn"
                  >
                    {'Iniciar validação por WhatsApp'}
                  </Button>
                </Link>

                <p className="text-center text-base font-semibold text-slate-700">
                  {'Não encontrou seu cadastro?'}{' '}
                  <button
                    type="button"
                    onClick={() => setActiveTab('new')}
                    className="font-bold text-accent underline decoration-accent/40 underline-offset-4 transition-colors hover:text-[#E89715]"
                  >
                    {'Faça um cadastro novo'}
                  </button>
                </p>
              </div>
            )}

            {/* Provider Form (Cadastro Novo) */}
            {activeTab === 'new' && (
              <form onSubmit={handleProviderSubmit} className="space-y-4" autoComplete="off">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="provider-trade">Nome</Label>
                    <Input
                      id="provider-trade"
                      placeholder="Nome do profissional ou clínica"
                      value={providerForm.trade_name}
                      onChange={(e) => setProviderForm({ ...providerForm, trade_name: e.target.value })}
                      required
                      data-testid="provider-trade"
                    />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label>Tipo de prestador</Label>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {PROVIDER_TYPE_OPTIONS.map((option) => {
                        const active = providerForm.professional_type === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => updateProviderType(option.value)}
                            className={`rounded-xl border px-3 py-2 text-left text-sm font-medium transition ${
                              active ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 bg-white text-slate-700 hover:border-primary/40'
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provider-state">Estado</Label>
                    <SearchableCombobox
                      open={stateOpen}
                      onOpenChange={setStateOpen}
                      items={states}
                      selectedValue={providerForm.state}
                      onSelect={(state) => setProviderForm({
                        ...providerForm,
                        state: state.uf,
                        city: '',
                        professional_registration_uf: state.uf,
                        technical_responsible_registration_uf: state.uf,
                      })}
                      onClear={() => setProviderForm({
                        ...providerForm,
                        state: '',
                        city: '',
                        professional_registration_uf: '',
                        technical_responsible_registration_uf: '',
                      })}
                      placeholder={loadingFilters ? 'Carregando...' : 'Selecione o estado'}
                      searchPlaceholder="Buscar estado..."
                      emptyMessage="Nenhum estado encontrado."
                      icon={MapPin}
                      disabled={loadingFilters}
                      displayValue={providerForm.state ? `${states.find((state) => state.uf === providerForm.state)?.name || providerForm.state} (${providerForm.state})` : null}
                      getItemKey={(state) => state.uf}
                      getItemLabel={(state) => `${state.name} (${state.uf})`}
                      getItemSearchValue={(state) => `${state.name} ${state.uf}`}
                      isSelected={(state) => providerForm.state === state.uf}
                      testId="provider-state"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provider-city">Cidade</Label>
                    <SearchableCombobox
                      open={cityOpen}
                      onOpenChange={setCityOpen}
                      items={cities}
                      selectedValue={providerForm.city}
                      onSelect={(city) => setProviderForm({ ...providerForm, city: city.city })}
                      onClear={() => setProviderForm({ ...providerForm, city: '' })}
                      placeholder={providerForm.state ? 'Selecione a cidade' : 'Escolha o estado primeiro'}
                      searchPlaceholder="Buscar cidade..."
                      emptyMessage={loadingCities ? 'Carregando cidades...' : 'Nenhuma cidade encontrada.'}
                      icon={Building2}
                      disabled={!providerForm.state || loadingCities}
                      displayValue={loadingCities ? 'Carregando...' : providerForm.city || null}
                      getItemKey={(city) => `${city.state || providerForm.state}-${city.city}`}
                      getItemLabel={(city) => city.city}
                      isSelected={(city) => providerForm.city === city.city}
                      testId="provider-city"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provider-phone">Telefone</Label>
                    <Input
                      id="provider-phone"
                      placeholder="(00) 00000-0000"
                      value={providerForm.phone}
                      onChange={(e) => setProviderForm({ ...providerForm, phone: formatPhone(e.target.value) })}
                      maxLength={15}
                      required
                      data-testid="provider-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provider-zip">CEP de atendimento</Label>
                    <Input
                      id="provider-zip"
                      placeholder="00000-000"
                      value={providerForm.zip_code}
                      onChange={(e) => {
                        const masked = formatCEP(e.target.value);
                        setProviderForm({ ...providerForm, zip_code: masked });
                        if (masked.replace(/\D/g, '').length === 8) lookupCep(masked);
                      }}
                      onBlur={(e) => lookupCep(e.target.value)}
                      maxLength={9}
                      required
                      data-testid="provider-zip"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provider-email">Email</Label>
                    <Input
                      id="provider-email"
                      type="email"
                      placeholder="contato@clinica.com"
                      value={providerForm.email}
                      onChange={(e) => setProviderForm({ ...providerForm, email: e.target.value })}
                      required
                      autoComplete="off"
                      data-testid="provider-email"
                    />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="provider-specialty">Especialidade</Label>
                    <SearchableCombobox
                      open={specialtyOpen}
                      onOpenChange={setSpecialtyOpen}
                      items={filteredSpecialties}
                      selectedValue={selectedSpecialty}
                      onSelect={(specialty) => {
                        setSelectedSpecialty(specialty);
                        setProviderForm({ ...providerForm, specialty: specialty.name, specialty_id: specialty.id });
                      }}
                      onClear={() => {
                        setSelectedSpecialty(null);
                        setProviderForm({ ...providerForm, specialty: '', specialty_id: '' });
                      }}
                      placeholder={loadingFilters ? 'Carregando...' : 'Selecione a especialidade'}
                      searchPlaceholder="Buscar especialidade..."
                      emptyMessage="Nenhuma especialidade compatível encontrada."
                      icon={Stethoscope}
                      disabled={loadingFilters}
                      displayValue={selectedSpecialty?.name}
                      getItemKey={(specialty) => specialty.id}
                      getItemLabel={(specialty) => specialty.name}
                      isSelected={(specialty) => selectedSpecialty?.id === specialty.id}
                      testId="provider-specialty"
                    />
                  </div>

                  {['medico', 'dentista'].includes(providerForm.professional_type) && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="provider-registration-number">
                          {providerForm.professional_type === 'dentista' ? 'CRO' : 'CRM'}
                        </Label>
                        <Input
                          id="provider-registration-number"
                          placeholder={providerForm.professional_type === 'dentista' ? 'Ex: 12345' : 'Ex: 12345'}
                          value={providerForm.professional_registration_number}
                          onChange={(e) => setProviderForm({ ...providerForm, professional_registration_number: e.target.value })}
                          required
                          data-testid="provider-registration-number"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="provider-registration-uf">
                          UF do {providerForm.professional_type === 'dentista' ? 'CRO' : 'CRM'}
                        </Label>
                        <select
                          id="provider-registration-uf"
                          value={providerForm.professional_registration_uf || providerForm.state}
                          onChange={(e) => setProviderForm({ ...providerForm, professional_registration_uf: e.target.value })}
                          className="flex h-11 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          required
                          data-testid="provider-registration-uf"
                        >
                          <option value="">UF</option>
                          {states.map((state) => <option key={state.uf} value={state.uf}>{state.uf}</option>)}
                        </select>
                      </div>
                    </>
                  )}

                  {['clinica_medica', 'clinica_odontologica'].includes(providerForm.professional_type) && (
                    <>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="technical-responsible-name">Responsável técnico</Label>
                        <Input
                          id="technical-responsible-name"
                          placeholder="Nome do responsável técnico"
                          value={providerForm.technical_responsible_name}
                          onChange={(e) => setProviderForm({ ...providerForm, technical_responsible_name: e.target.value })}
                          data-testid="technical-responsible-name"
                        />
                      </div>

                      <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-3 sm:col-span-2">
                        <div className="space-y-2">
                          <Label htmlFor="technical-registration-number">
                            {providerForm.professional_type === 'clinica_odontologica' ? 'CRO do responsável' : 'CRM do responsável'}
                          </Label>
                          <Input
                            id="technical-registration-number"
                            placeholder="Ex: 12345"
                            value={providerForm.technical_responsible_registration_number}
                            onChange={(e) => setProviderForm({ ...providerForm, technical_responsible_registration_number: e.target.value })}
                            data-testid="technical-registration-number"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="technical-registration-uf">UF</Label>
                          <select
                            id="technical-registration-uf"
                            value={providerForm.technical_responsible_registration_uf || providerForm.state}
                            onChange={(e) => setProviderForm({ ...providerForm, technical_responsible_registration_uf: e.target.value })}
                            className="flex h-11 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid="technical-registration-uf"
                          >
                            <option value="">UF</option>
                            {states.map((state) => <option key={state.uf} value={state.uf}>{state.uf}</option>)}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="provider-cnpj">CNPJ</Label>
                    <Input
                      id="provider-cnpj"
                      placeholder="00.000.000/0000-00"
                      value={providerForm.cnpj}
                      onChange={(e) => setProviderForm({ ...providerForm, cnpj: formatCNPJ(e.target.value) })}
                      maxLength={18}
                      required={['clinica_medica', 'clinica_odontologica'].includes(providerForm.professional_type)}
                      data-testid="provider-cnpj"
                    />
                    {['medico', 'dentista'].includes(providerForm.professional_type) && (
                      <p className="text-xs text-slate-500">Se você atende como pessoa jurídica, informe também seu CNPJ.</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provider-password">Senha</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="provider-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Mínimo 6 caracteres"
                        value={providerForm.password}
                        onChange={(e) => setProviderForm({ ...providerForm, password: e.target.value })}
                        className="pl-10 pr-10"
                        minLength={6}
                        required
                        autoComplete="new-password"
                        data-testid="provider-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-primary hover:bg-primary/90 mt-6"
                  disabled={loading}
                  data-testid="provider-submit"
                >
                  {loading ? 'Cadastrando...' : 'Cadastrar Clínica/Profissional'}
                </Button>
              </form>
            )}

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              {'Já tem uma conta?'}{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                {'Faça login'}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

